import Typography from '@mui/material/Typography/Typography';

import { colors } from 'styles/colors';

import { ICustomerCaseData } from 'types/customer-case';

import BlueHelicopterImage from 'assets/customer-cases/blue-helicopter.webp';

import HelicopterIcon from 'assets/customer-cases/helicopter.svg';
import SafetyCheckIcon from 'assets/customer-cases/safety-check.svg';
import WhiteSimulationIcon from 'assets/customer-cases/simulation.svg';
import RainySnowyIcon from 'assets/industries/rainy-snowy-night.svg';

import ThreeHelicoptersInFieldImage from 'assets/common/images/aeronautics-use-case-3.webp';
import HelicopterWithThreeDronesImage from 'assets/customer-cases/air-bus-helicopter/helicopter-with-three-drones.webp';
import ThreeHelicoptersImage from 'assets/customer-cases/air-bus-helicopter/three-helicopters.webp';
import TwoHelicoptersInForest from 'assets/customer-cases/air-bus-helicopter/two-helicopters-in-forest.webp';

import WhiteSensorsImage from 'assets/white-sensor.svg';
import WhiteSpaceWarpsImage from 'assets/industries/space-warps.svg';
import WhiteMoonAndRockImage from 'assets/moon-and-rock.svg';
import WhiteAutomationIcon from 'assets/white-automation.svg';
import WhiteRadarIcon from 'assets/industries/radar.svg';
import { intelligentDataPipelineApplicationMeta } from 'constants/pages';

import HeroImage from 'assets/applications/app-2.0.webp';

export const firstCustomerCaseData: ICustomerCaseData = {
  heroData: {
    heroTitle: 'Advancing Helicopter Safety through Next-Gen Simulation',
    heroDescription: `
    Learn how a helicopter OEM used AILiveSim to train, test, and validate a next-generation anti-collision system in 
    highly complex and variable environments. This simulation-based approach enabled testing across high-risk scenarios 
    previously considered untestable. Experience the future of safety in aviation with our innovative simulation technology.
    `,
    heroListTitle: 'Transforming Helicopter Safety with Advanced Simulation Technology',
    heroListItems: [
      {
        Icon: HelicopterIcon,
        description:
          'AILiveSim enabled groundbreaking testing for a leading helicopter manufacturer.',
        title: 'Leading OEM Leverages AI for Next-Level Anti-Collision System Validation',
      },
      {
        Icon: RainySnowyIcon,
        description:
          'Our simulation technology eliminates the risks and costs of traditional testing.',
        title: 'Innovative Simulation Solutions for Helicopter Safety Enhancements',
      },
      {
        Icon: SafetyCheckIcon,
        title: 'Comprehensive Validation for AI-Driven Safety Systems in Aviation',
        description: 'Experience unparalleled realism in testing diverse operational scenarios.',
      },
    ],
    buttonLabel: 'Explore Navy & Defense Industry ',
    buttonLink: '/Industries/navy-defence',
    heroImages: {
      firstImage: {
        imageSrc: ThreeHelicoptersInFieldImage,
        altText: 'Three helicopters in field',
      },
      secondImage: {
        imageSrc: HelicopterWithThreeDronesImage,
        altText: 'Helicopter with three drones',
      },
      thirdImage: {
        imageSrc: ThreeHelicoptersImage,
        altText: 'Three helicopters',
      },
      fourthImage: {
        imageSrc: TwoHelicoptersInForest,
        altText: 'Two helicopters in forest',
      },
    },
  },
  customerCase: {
    title: 'The Challenge: Testing the Untestable Anti-Collision System in Realistic Conditions',
    description: (
      <>
        <Typography fontSize="18px" color={colors.mainDark}>
          To validate a next-gen anti-collision system, real-world testing posed significant dangers
          and costs. The OEM needed to validate an anti-collision system using black-box AI models.
          Real-world testing couldn&apost capture the full operational design domain (ODD), especially
          across variable weather, lighting, and terrain conditions.
        </Typography>

        <Typography fontSize="18px" color={colors.mainDark} marginTop="16px">
          Traditional physic-based simulators lack the variability and realism needed to support
          such a wide range of conditions and possible permutations. The customer was looking for a
          simulator that could simulate all sensors in the same clocktime to allow sensor fusion,
          compare the capability of combined vs individual sensors performance per conditions,
          distance or obstacle. Additionally the objective is to prepare the ground for validation
          and certificability of such a system using blackbox AI models for airborne equipment.
        </Typography>
      </>
    ),
    image: BlueHelicopterImage,
    bottomText: '',
    items: [],
  },
  firstInformationBlockData: {
    title: 'Simulate the Impossible with AILiveSim',
    description:
      'With AILiveSim, the OEM was able to safely train and evaluate a next-generation anti-collision system without the high cost and risk of real-world testing. The simulation enabled real-time validation across diverse weather, terrain, and obstacle scenarios using multi-sensor technologies like cameras, LiDAR, and radar.',
    items: [
      {
        Icon: WhiteSpaceWarpsImage,
        title: 'Dynamic Weather & Visibility',
        description:
          'Weather conditions and visibility: rain, fog, low visibility, night time & dawn/dusk',
      },
      {
        Icon: WhiteMoonAndRockImage,
        title: 'Diverse Terrains',
        description: 'Landscapes: hills, urban, forested & grass lands, desertic',
      },
      {
        Icon: WhiteSimulationIcon,
        title: 'Real-World Obstacles',
        description: 'Obstacles: cables & towers, various flying objects, trees, buildings',
      },
      {
        Icon: WhiteSensorsImage,
        title: 'Multi-Sensor \nSimulation',
        description: 'Multi sensors technologies including cameras, LiDAR and radars',
      },
    ],
    link: '/platform',
    linkLabel: 'Learn how the platform works',
  },
  resultsData: {
    title: 'Results: Transforming Risky Trials into Reliable Insights for Helicopter Safety',
    clientFeedbackMessage: `
    “AILiveSim allowed us to simulate the unpredictable—from night-time flights near power lines to 
    low-visibility urban environments. It's the only solution that offered the variability and realism 
    our project demanded.”
    `,
    clientLabel: '— Thomas K., Program Lead, Helicopter OEM',
    description:
      'Our innovative approach enabled comprehensive testing across the full Operational Design Domain (ODD). This led to significant cost reductions and enhanced safety measures.',
    listItems: [
      <li key="1" style={{ lineHeight: '140%', fontSize: '16px', marginBottom: '16px' }}>
        Paved the way for system certification using AI models
      </li>,
      <li key="2" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Enabled simulation of edge cases and unpredictable scenarios,
      </li>,
    ],
    listTitle: 'Key Outcomes',
    resultTitle: 'Reduce Costs and Risks with Advanced Simulation Technology',
    resultDescription: 'Eliminate the dangers of traditional field trials and save resources.',
  },
  secondInformationBlockData: {
    items: [
      {
        Icon: WhiteSimulationIcon,
        description:
          'Full-environment variability: Any weather, time of day, terrain type, or obstacle configuration',
        title: 'Adapted to Every Environment',
      },
      {
        Icon: WhiteSensorsImage,
        description:
          'Multi-sensors support: Real-time simulation for radar, LiDAR, IR and visible cameras',
        title: 'Enabled Seamless Multi-Sensor Fusion',
      },
      {
        Icon: WhiteAutomationIcon,
        description:
          'AI-in-the-loop: train and test perception and decision-making pipelines, not just raw sensor data',
        title: 'Validated AI Decision-Making Pipelines',
      },
      {
        Icon: WhiteRadarIcon,
        description:
          'Hyper spectral realism: combination of physics fidelity and visual effects, sensor defect & noise for high -quality sensor data in different frequency bands',
        title: 'Achieved Hyper-Realistic Sensor Outputs',
      },
    ],
    description:
      "AILiveSim's next-generation simulation environment combines the latest Unreal Engine's visual realism with physics-based logic and AI-ready workflows. This allows the OEM to simulate previously untestable scenarios, like low-altitude flights in bad weather near power lines, at dusk, and under drone collision threats.",
    title: 'Simulation Capabilities for Advanced Helicopter Safety and Performance',
  },
  applicationData: {
    description:
      'Collecting and labeling real-world data is slow, costly, and rarely captures the full complexity of real environments. With AILiveSim 2.0, you can build a smarter data strategy by integrating intelligent simulation into your pipeline — generating high-quality synthetic data on demand, improving model accuracy, reducing costs, and accelerating development at scale.',
    image: HeroImage,
    link: intelligentDataPipelineApplicationMeta.url,
    title: 'Learn more about Intelligent Data Pipeline with Simulation-in-the-Loop',
    bottomText: '',
    items: [],
  },
};
